import { environment } from '@hakimo-ui/hakimo/environments';

export type FeedbackType = 'Bug' | 'Feedback' | 'Issue';

export interface RadioItem {
  id: FeedbackType;
  name: string;
}

export interface SlackMessagePayload {
  text: string;
  blocks: unknown[];
}

export const radioItems: RadioItem[] = [
  { id: 'Bug', name: 'Bug' },
  { id: 'Feedback', name: 'Feedback' },
  { id: 'Issue', name: 'Issue' },
];

const ICONS: Record<FeedbackType, string> = {
  Bug: '🐛',
  Feedback: '💬',
  Issue: '⚠️',
};

// Hard limit on a Slack section block.
const MAX_SECTION_LENGTH = 3000;
const TRUNCATION_MARKER = '\n> …truncated';

const WEBHOOK_PREFIX = 'https://hooks.slack.com/';

// getEnvValueAsString returns this when a key is absent from env.js, which is
// the case for the admin and ab-testing bundles.
const MISSING = 'UNKNOWN';

// Slack parses these three as markup in mrkdwn text, so a pasted HTML snippet
// would silently vanish from the report and a pasted <!channel> would become a
// live notification. https://docs.slack.dev/messaging/formatting-message-text
function escapeMrkdwn(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function isFeedbackEnabled(): boolean {
  return environment.feedbackSlackWebhook.startsWith(WEBHOOK_PREFIX);
}

export function isValidFeedback(text: string): boolean {
  return text.trim().length > 0;
}

// "U123 U456" -> "<@U123> <@U456>". Space separated because setenv.sh cannot
// carry a comma. Anything already wrapped in <> (user groups, <!here>) passes
// through untouched.
export function renderMentions(raw: string): string {
  if (raw === MISSING) {
    return '';
  }
  return raw
    .split(/\s+/)
    .filter(Boolean)
    .map((id) => (id.startsWith('<') ? id : `<@${id}>`))
    .join(' ');
}

export function getFeedbackPayload(
  type: FeedbackType,
  text: string,
  email: string
): SlackMessagePayload {
  // Quote first, then cap: the "> " prefixes are 2 chars per line, so capping
  // the raw text would still overflow the block on a multi-line report and
  // Slack would reject the whole message.
  const quoted = escapeMrkdwn(text)
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n');
  const report =
    quoted.length > MAX_SECTION_LENGTH
      ? quoted.slice(0, MAX_SECTION_LENGTH - TRUNCATION_MARKER.length) +
        TRUNCATION_MARKER
      : quoted;
  const mentions = renderMentions(environment.feedbackSlackMentions);

  return {
    // Fallback for notifications and clients that cannot render blocks.
    text: `${type} from ${email}: ${escapeMrkdwn(text).slice(0, 120)}`,
    blocks: [
      {
        type: 'header',
        text: { type: 'plain_text', text: `${ICONS[type]} ${type} in UAT` },
      },
      { type: 'section', text: { type: 'mrkdwn', text: report } },
      {
        type: 'context',
        elements: [
          {
            type: 'mrkdwn',
            text: `*From:* ${email}   *Build:* \`${environment.release}\`   *Page:* <${window.location.href}|${window.location.pathname}>`,
          },
        ],
      },
      // Mentions live in a section, not the context block -- context renders
      // the link but does not reliably notify.
      ...(mentions
        ? [
            {
              type: 'section',
              text: { type: 'mrkdwn', text: `cc ${mentions}` },
            },
          ]
        : []),
      { type: 'divider' },
    ],
  };
}
